import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';

import { DeviceDetectorService } from 'ngx-device-detector';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { UserService } from './services/user/user.service';
import { environment } from '@environment/environment';
import { FronteggAppService, FronteggAuthService } from '@frontegg/angular';
import { TracingService } from './services/tracing/tracing.service';
import { NavigationEnd } from '@angular/router';
import { Router } from '@angular/router';
import { PosthogService } from './core/services/posthog/posthog.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [Location, { provide: LocationStrategy, useClass: PathLocationStrategy }],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  isMobile: boolean = false;
  isAuthenticated?: boolean;
  isLoading?: boolean;
  isBillingOnly;
  onMaintenance: boolean = false;
  private _ngUnsubscribe$ = new Subject<any>();

  constructor(
    private tracingService: TracingService,
    private deviceService: DeviceDetectorService,
    public dialog: MatDialog,
    private fronteggAppAuthService: FronteggAuthService,
    private fronteggAppService: FronteggAppService,
    private userService: UserService,
    private router: Router,
    private posthogService: PosthogService,
  ) {
    this.isMobile = this.deviceService.isMobile();
    this.listenToInvitation();
  }

  ngOnInit(): void {
    if (environment.ON_MAINTENANCE) {
      this.onMaintenance = true;
      return;
    }

    this.posthogService.init();

    this.fronteggAppAuthService?.isAuthenticated$.pipe(takeUntil(this._ngUnsubscribe$)).subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
    });
    this.fronteggAppService.isLoading$.pipe(takeUntil(this._ngUnsubscribe$)).subscribe((load) => {
      this.isLoading = load;
    });
    this.userService.currentPdqUser$.pipe(takeUntil(this._ngUnsubscribe$)).subscribe((user) => {
      // if user has only billing admin role
      this.isBillingOnly =
        user?.roles.find((r) => r.key === 'BILLING_ADMIN') && !user.role.find((r) => r.key != 'BILLING_ADMIN');
    });

    //hotfix for display columns, can be deleted in future
    const orderNumColumnShown = localStorage.getItem('order-number-column-shown');
    const colorColumnShown = localStorage.getItem('color-coding-column-shown');
    if (!orderNumColumnShown || !colorColumnShown) {
      const tablesColumns = ['validate-columns', 'assign-columns', 'fulfill-columns', 'track-columns'];
      tablesColumns.forEach((table) => {
        const data = localStorage.getItem(table);
        if (data) {
          const columns = JSON.parse(data);
          orderNumColumnShown || columns.push('orderNumber');
          colorColumnShown || columns.push('colorCoding');
          localStorage.setItem(table, JSON.stringify(columns));
        }
      });
      orderNumColumnShown || localStorage.setItem('order-number-column-shown', '' + true);
      colorColumnShown || localStorage.setItem('color-coding-column-shown', '' + true);
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe$.next(true);
    this._ngUnsubscribe$.complete();
  }

  onContinue(event: boolean): void {
    this.isMobile = event;
  }

  listenToInvitation() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const url = event.urlAfterRedirects;

      // 1. If on /account/invitation/accept, set a flag
      if (url.includes('/account/invitation/accept')) {
        localStorage.setItem('shouldRefreshOnNextRoute', 'true');
      } else {
        // 2. If flag is set and not on /account/invitation/accept, refresh and clear flag
        if (localStorage.getItem('shouldRefreshOnNextRoute') === 'true') {
          localStorage.removeItem('shouldRefreshOnNextRoute');
          window.location.reload();
        }
      }
    });
  }
}
